import React from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import { get, has } from 'lodash';
import { connect } from 'react-redux';

// Models
import { CmsEntities, bindToContent } from 'client/data/models/cms';

// Utils
import { getBreadcrumbName, getUrlSlugByName } from 'site-modules/shared/utils/upper-funnel/type-mapping';
import { getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getCustomSubtypeTitle } from 'site-modules/upper-funnel/utils/seo-content';

// eslint-disable-next-line venom/no-internal-modules
import { NotFoundUI } from 'site-modules/venom/pages/not-found/not-found';
import { TopRatedPromoUnit } from 'site-modules/shared/components/top-rated-promo-unit/top-rated-promo-unit';
import { HeroContent } from 'site-modules/upper-funnel/components/type/hero-content/hero-content';
import { withCompareVehicleTray } from 'site-modules/shared/components/compare-vehicle-tray-wrapper/compare-vehicle-tray-wrapper';
import { VehicleTypeRankingsList } from 'site-modules/upper-funnel/components/type/vehicle-type-rankings-list/vehicle-type-rankings-list';
import { getSubtypeDisplayTitle } from 'site-modules/upper-funnel/utils/subtype-display';
import {
  getIndicesLength,
  getSubtypeOverflowAdPos,
} from 'site-modules/upper-funnel/pages/subtype/get-subtype-overflow-ad-pos';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { MostSoldTypeInfographic } from 'site-modules/shared/components/upper-funnel/most-sold-type-infographic/most-sold-type-infographic';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { hasMostSoldTypeContent } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { mappedIndicesForSubtypeAdPlacement } from 'site-modules/upper-funnel/pages/subtype/subtype';
import { TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { getSubtypeContentPath } from 'site-modules/shared/utils/upper-funnel/content-path';
import { getFirstObjectIfArray } from './best-rankings-definition';
import './best-rankings.scss';

export const VehicleTypeRankingsListWithCompareTray = withCompareVehicleTray(VehicleTypeRankingsList, {
  backFocusLink: {
    label: 'Skip to vehicle rankings',
    selector: '.vehicle-card .compare-checkbox input',
  },
});

const getRefererPathName = referer => (referer ? get(referer.match(/edmunds.com(.*)/), '[1]', '') : '');

export function bestRankingsReturnedNoData(bestRankings) {
  return (
    bestRankings !== undefined && has(bestRankings, 'rankings') && !Object.keys(get(bestRankings, 'rankings')).length
  );
}

export function BestRankingsPageUI(props) {
  const { isMobile, referer, bestRankings, content } = props;
  const rankings = getFirstObjectIfArray(get(bestRankings, 'rankings', {}));
  if (bestRankingsReturnedNoData(bestRankings)) {
    return <NotFoundUI />;
  }
  const subtypeAdPlacementIndices = mappedIndicesForSubtypeAdPlacement([rankings]);
  const type = get(rankings, 'edmundsTypeCategory', '').toLowerCase();
  const subtype = get(rankings, 'subtype');
  const subtypeExpanisonName = get(rankings, 'displayName');
  const title = getCustomSubtypeTitle(type, subtype, subtypeExpanisonName);
  const mostSoldTypeUniqueName = `${subtype}-${type}s`.toLowerCase();
  const mostSoldTypeGraphTitle = hasMostSoldTypeContent(type, subtype);
  const subheadTitle = getSubtypeDisplayTitle(type, subtype, true);
  const breadcrumbsData =
    type && subtype
      ? [
          getHomeBreadcrumb(),
          {
            title: getBreadcrumbName(type),
            href: `/${getUrlSlugByName(type)}`,
          },
          {
            title: subtype.replace(/suvs/g, 'SUVs'),
          },
        ]
      : [];
  const trkParam = getRefererPathName(referer) === '/' ? { trk: type } : {};

  let description;
  if (content) {
    description = content.metadata('description').value();
  }

  return (
    <main id="main-content" className="type-page-v2 subtype-page-v2" role="main" aria-labelledby="type-page-heading">
      <HeroContent
        breadcrumbsData={breadcrumbsData}
        isMobile={isMobile}
        type={type}
        title={title}
        isChildPage
        subtypeTitle={subheadTitle}
      />
      <Container className="mb-2">
        <TopRatedPromoUnit className="my-2" isMobile={isMobile} />
        <Row>
          <Col xs={12}>
            <VehicleTypeRankingsListWithCompareTray
              type={type}
              trkParam={trkParam}
              vehicleEditorialRankings={rankings ? [rankings] : []}
              isSubtypePage
              isSubtypeExpansionPage
              subtypeAdPlacementIndices={subtypeAdPlacementIndices}
            />
          </Col>
          <Col xs={12} className="d-flex flex-column">
            {getSubtypeOverflowAdPos(getIndicesLength(subtypeAdPlacementIndices), trkParam, isMobile, type)}
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            {mostSoldTypeGraphTitle && (
              <ScrollElement id={`best-selling-${mostSoldTypeUniqueName}`}>
                <RenderWhenViewable
                  verticalOffset="100%"
                  placeholder={
                    <MostSoldTypeInfographic type={mostSoldTypeUniqueName} isMobile={isMobile} showMap={false} />
                  }
                >
                  <MostSoldTypeInfographic type={mostSoldTypeUniqueName} isMobile={isMobile} />
                </RenderWhenViewable>
              </ScrollElement>
            )}
            {description && (
              <ContentFragment classes="text-gray-darker pb-3 description">{description}</ContentFragment>
            )}
          </Col>
        </Row>
      </Container>
    </main>
  );
}

BestRankingsPageUI.propTypes = {
  isMobile: PropTypes.bool,
  referer: PropTypes.string,
  bestRankings: PropTypes.shape({
    rankings: TypeRankingsEntities,
  }),
  content: CmsEntities.Content,
};

BestRankingsPageUI.defaultProps = {
  isMobile: false,
  referer: '',
  bestRankings: {},
  content: null,
};

export const stateToPropsConfig = {
  content: bindToContent(props => {
    const rankings = get(props, 'bestRankings.rankings');
    const type = get(rankings, 'edmundsTypeCategory', '').toLowerCase();
    const subtype = get(rankings, 'subtype');
    return getSubtypeContentPath(type, subtype);
  }),
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  referer: get(state.pageContext.page, 'referer'),
  bestRankings: get(state, 'seo.bestRankings', {}),
});

export const BestRankingsPage = connect(mapStateToProps)(connectToModel(BestRankingsPageUI, stateToPropsConfig));
